import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SectionStamp } from '../components/Atoms';
import Icon from '../components/Icon';
import DesktopHeader from '../components/DesktopHeader';
import DesktopFooter from '../components/DesktopFooter';
import DesktopWhatsAppFab from '../components/DesktopWhatsAppFab';
import { fonts } from '../theme';

const SERVICES = ['Orthopaedic', 'Neurological', 'Paediatric', 'Geriatric', 'Post-op rehab'];

const DAYS = [
  { d: 12, w: 'Tue', dis: true },
  { d: 13, w: 'Wed', dis: false },
  { d: 14, w: 'Thu', dis: false },
  { d: 15, w: 'Fri', dis: false },
  { d: 16, w: 'Sat', dis: false },
  { d: 17, w: 'Sun', dis: true },
  { d: 18, w: 'Mon', dis: false },
];

const TIMES = ['09:00', '10:30', '12:00', '14:30', '16:00', '17:30'];

const STEP_LABELS = ['Care', 'Slot', 'Details', 'Confirm'];

export default function BookingDesktop({ t }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [picked, setPicked] = useState({
    service: 'Orthopaedic',
    where: 'Clinic',
    date: 14,
    time: '10:30',
    name: '', phone: '', notes: '',
  });

  const update = (k, v) => setPicked(p => ({ ...p, [k]: v }));

  return (
    <div style={{
      width: '100%', minHeight: '100vh', background: t.paper, color: t.ink,
      fontFamily: fonts.sans, position: 'relative', overflow: 'hidden',
    }}>
      <DesktopHeader t={t} />

      <div style={{ padding: '60px max(56px, 4vw) 32px', maxWidth: 1440, margin: '0 auto' }}>
        <SectionStamp n={String(step + 1).padStart(2, '0') + ' / 04'} label={STEP_LABELS[step]} t={t}/>
        <h1 style={{
          fontFamily: fonts.serif, fontSize: 'clamp(48px, 6vw, 80px)', fontWeight: 300,
          letterSpacing: '-0.04em', lineHeight: 0.92, margin: '20px 0 0',
          textWrap: 'balance',
        }}>
          {step === 0 && <>What brings <span style={{ fontStyle: 'italic' }}>you</span> in?</>}
          {step === 1 && <>When works <span style={{ fontStyle: 'italic' }}>best?</span></>}
          {step === 2 && <>A few <span style={{ fontStyle: 'italic' }}>details.</span></>}
          {step === 3 && <>See you on <span style={{ fontStyle: 'italic' }}>Thursday.</span></>}
        </h1>
        <div style={{ display: 'flex', gap: 6, marginTop: 32, maxWidth: 480 }}>
          {[0, 1, 2, 3].map(i => (
            <div key={i} style={{
              flex: 1, height: 2, borderRadius: 2,
              background: i <= step ? t.ink : t.hair,
              transition: 'background .2s',
            }}/>
          ))}
        </div>
      </div>

      <div style={{
        padding: '24px max(56px, 4vw) 80px', maxWidth: 1440, margin: '0 auto',
        display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 56, alignItems: 'start',
      }}>
        <div>
          {step === 0 && (
            <>
              <SectionStamp n="01" label="Choose care" t={t}/>
              <p style={{
                fontFamily: fonts.sans, fontSize: 14, color: t.muted, lineHeight: 1.6,
                margin: '12px 0 24px', maxWidth: 540,
              }}>
                Pick the closest match. We&rsquo;ll refine the plan in your first consult.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, maxWidth: 720 }}>
                {SERVICES.map(s => {
                  const on = picked.service === s;
                  return (
                    <button key={s} onClick={() => update('service', s)} style={{
                      appearance: 'none', textAlign: 'left', cursor: 'pointer',
                      background: on ? t.ink : 'transparent', color: on ? t.paper : t.ink,
                      border: `0.5px solid ${on ? t.ink : t.hair}`,
                      borderRadius: 14, padding: '20px 22px',
                      fontFamily: fonts.serif, fontSize: 22, fontWeight: 400, letterSpacing: '-0.02em',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    }}>
                      <span>{s}</span>
                      {on && <Icon name="check" size={18} stroke={t.mint}/>}
                    </button>
                  );
                })}
              </div>

              <div style={{ marginTop: 40 }}>
                <SectionStamp n="02" label="Where" t={t}/>
                <div style={{
                  display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12,
                  marginTop: 16, maxWidth: 520,
                }}>
                  {[
                    { id: 'Clinic', sub: 'Vadodara', ic: 'pin' },
                    { id: 'Home', sub: 'Within Vadodara', ic: 'home' },
                  ].map(opt => {
                    const on = picked.where === opt.id;
                    return (
                      <button key={opt.id} onClick={() => update('where', opt.id)} style={{
                        appearance: 'none', textAlign: 'left', cursor: 'pointer',
                        background: on ? t.mint : 'transparent', color: t.ink,
                        border: `0.5px solid ${on ? t.mint : t.hair}`,
                        borderRadius: 14, padding: 20,
                      }}>
                        <Icon name={opt.ic} size={22} sw={1.6}/>
                        <div style={{ fontFamily: fonts.serif, fontSize: 24, marginTop: 12 }}>{opt.id}</div>
                        <div style={{
                          fontFamily: fonts.mono, fontSize: 10, color: t.muted,
                          letterSpacing: '0.16em', textTransform: 'uppercase', marginTop: 4,
                        }}>{opt.sub}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {step === 1 && (
            <>
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                marginBottom: 16, maxWidth: 720,
              }}>
                <div style={{ fontFamily: fonts.serif, fontSize: 22 }}>May 2026</div>
                <div style={{
                  fontFamily: fonts.mono, fontSize: 10, color: t.muted,
                  letterSpacing: '0.18em', textTransform: 'uppercase',
                }}>This week</div>
              </div>

              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 8, maxWidth: 720,
              }}>
                {DAYS.map(day => {
                  const on = picked.date === day.d;
                  return (
                    <button
                      key={day.d}
                      disabled={day.dis}
                      onClick={() => !day.dis && update('date', day.d)}
                      style={{
                        appearance: 'none', cursor: day.dis ? 'not-allowed' : 'pointer',
                        background: on ? t.ink : 'transparent',
                        color: day.dis ? t.muted + '66' : on ? t.paper : t.ink,
                        border: `0.5px solid ${on ? t.ink : t.hair}`,
                        borderRadius: 12, padding: '14px 0', textAlign: 'center',
                      }}
                    >
                      <div style={{
                        fontFamily: fonts.mono, fontSize: 9.5, letterSpacing: '0.16em',
                        textTransform: 'uppercase', opacity: day.dis ? 0.4 : 0.7,
                      }}>{day.w}</div>
                      <div style={{
                        fontFamily: fonts.serif, fontSize: 26, marginTop: 6,
                        textDecoration: day.dis ? 'line-through' : 'none',
                      }}>{day.d}</div>
                    </button>
                  );
                })}
              </div>

              <div style={{ marginTop: 32, maxWidth: 720 }}>
                <div style={{
                  fontFamily: fonts.mono, fontSize: 10, color: t.muted,
                  letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: 12,
                }}>Available times</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 8 }}>
                  {TIMES.map(time => {
                    const on = picked.time === time;
                    return (
                      <button key={time} onClick={() => update('time', time)} style={{
                        appearance: 'none', cursor: 'pointer',
                        background: on ? t.mint : 'transparent', color: t.ink,
                        border: `0.5px solid ${on ? t.mint : t.hair}`,
                        borderRadius: 12, padding: '14px 0',
                        fontFamily: fonts.serif, fontSize: 18, fontWeight: 400,
                      }}>{time}</button>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {step === 2 && (
            <div style={{ maxWidth: 560 }}>
              {[
                { k: 'name', label: 'Full name', placeholder: 'Anjali Rao' },
                { k: 'phone', label: 'Phone', placeholder: '+91 ' },
                { k: 'notes', label: 'What hurts? (optional)', placeholder: 'A short note helps me prepare.', multi: true },
              ].map(f => (
                <div key={f.k} style={{ marginBottom: 18 }}>
                  <label style={{
                    fontFamily: fonts.mono, fontSize: 10, color: t.muted,
                    letterSpacing: '0.18em', textTransform: 'uppercase',
                    display: 'block', marginBottom: 8,
                  }}>
                    {f.label}
                  </label>
                  {f.multi ? (
                    <textarea
                      value={picked[f.k]}
                      onChange={e => update(f.k, e.target.value)}
                      placeholder={f.placeholder}
                      style={{
                        width: '100%', minHeight: 110, padding: 14,
                        background: 'transparent', color: t.ink,
                        border: `0.5px solid ${t.hair}`, borderRadius: 12,
                        fontFamily: fonts.sans, fontSize: 15, resize: 'none', outline: 'none',
                      }}
                    />
                  ) : (
                    <input
                      value={picked[f.k]}
                      onChange={e => update(f.k, e.target.value)}
                      placeholder={f.placeholder}
                      style={{
                        width: '100%', padding: 16,
                        background: 'transparent', color: t.ink,
                        border: `0.5px solid ${t.hair}`, borderRadius: 12,
                        fontFamily: fonts.sans, fontSize: 15, outline: 'none',
                      }}
                    />
                  )}
                </div>
              ))}
            </div>
          )}

          {step === 3 && (
            <div style={{ maxWidth: 560 }}>
              <div style={{
                width: 80, height: 80, borderRadius: 999, background: t.mint,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: 24,
              }}>
                <Icon name="check" size={36} stroke={t.ink} sw={2}/>
              </div>
              <p style={{
                fontFamily: fonts.sans, fontSize: 15, color: t.muted, lineHeight: 1.6,
                margin: '0 0 32px',
              }}>
                You&rsquo;ll get a WhatsApp confirmation now and a reminder the day before.
              </p>
              <button
                onClick={() => { setStep(0); navigate('/'); }}
                style={{
                  appearance: 'none', border: `0.5px solid ${t.hair}`, background: 'transparent',
                  cursor: 'pointer', padding: '14px 22px', borderRadius: 999,
                  fontFamily: fonts.sans, fontSize: 14, fontWeight: 500, color: t.ink,
                }}
              >Back to home</button>
            </div>
          )}

          {step < 3 && (
            <div style={{ marginTop: 40, display: 'flex', gap: 12 }}>
              {step > 0 && (
                <button onClick={() => setStep(s => s - 1)} style={{
                  appearance: 'none', border: `0.5px solid ${t.hair}`, background: 'transparent',
                  color: t.ink, cursor: 'pointer', padding: '16px 28px', borderRadius: 999,
                  fontFamily: fonts.sans, fontSize: 14, fontWeight: 500,
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                }}>
                  <Icon name="arrow-left" size={16} stroke={t.ink}/>
                  <span>Back</span>
                </button>
              )}
              <button onClick={() => setStep(s => s + 1)} style={{
                appearance: 'none', border: 'none', cursor: 'pointer',
                background: t.ink, color: t.paper,
                padding: '16px 28px', borderRadius: 999,
                fontFamily: fonts.sans, fontSize: 14, fontWeight: 500,
                display: 'inline-flex', alignItems: 'center', gap: 10,
              }}>
                <span>{step === 0 ? 'Continue' : step === 1 ? `${picked.time} on the ${picked.date}th` : 'Confirm booking'}</span>
                <Icon name="arrow-right" size={16}/>
              </button>
            </div>
          )}
        </div>

        <div style={{
          padding: 32, borderRadius: 18, border: `0.5px solid ${t.hair}`,
          position: 'sticky', top: 24, background: t.cream,
        }}>
          <div style={{
            fontFamily: fonts.mono, fontSize: 10, color: t.muted,
            letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: 16,
          }}>Your booking</div>
          {[
            ['Care', picked.service],
            ['Where', `${picked.where} · Vadodara`],
            ['When', `Thu, May ${picked.date} · ${picked.time}`],
            ['Duration', '45 min · first consult'],
          ].map(([k, v]) => (
            <div key={k} style={{
              padding: '14px 0', borderBottom: `0.5px solid ${t.hair}`,
            }}>
              <div style={{
                fontFamily: fonts.mono, fontSize: 9.5, color: t.muted,
                letterSpacing: '0.16em', textTransform: 'uppercase',
              }}>{k}</div>
              <div style={{
                fontFamily: fonts.serif, fontSize: 18, marginTop: 4,
              }}>{v}</div>
            </div>
          ))}
          <div style={{
            marginTop: 16, fontFamily: fonts.sans, fontSize: 12, color: t.muted, lineHeight: 1.55,
          }}>
            You can reschedule with one tap from the WhatsApp confirmation.
          </div>
        </div>
      </div>

      <DesktopFooter t={t} />
      <DesktopWhatsAppFab />
    </div>
  );
}
